import { useMemo, useState } from 'react'

import { IconMessageCircle2, IconSwitch3 } from '@tabler/icons-react'
import moment from 'moment'

import BuilderSelectModal from '@/components/BuilderSelectModal'
import { ErrorLayout } from '@/components/layout-components/ErrorLayout'
import { Button } from '@/components/shared-components/BUI/Button'
import { Text } from '@/components/shared-components/BUI/Text'
import { Flexbox } from '@/components/shared-components/Flexbox'
import { openLiveChat } from '@/components/util-components/live-chat/LiveChat'
import { useUser } from '@/context/userContext'
import { useHasRoles } from '@/hooks/useHasRoles'
import { Role } from '@/modules/adminUsers/roles'

type Props = {
  children: React.ReactNode
}

export const ExpiredTrialLayout: React.FC<Props> = ({ children }) => {
  const { builder, builders } = useUser()
  const isSuperAdmin = useHasRoles(Role.SuperAdmin)
  const [isSwitchModalOpen, setIsSwitchModalOpen] = useState(false)

  const showTrialExpired = useMemo(() => {
    return (
      !isSuperAdmin &&
      builder?.isTrial &&
      builder?.trialExpiresAt &&
      moment().isAfter(builder.trialExpiresAt)
    )
  }, [builder?.isTrial, builder.trialExpiresAt, isSuperAdmin])

  const canSwitchBuilder = useMemo(() => {
    return builders && builders.length > 1
  }, [builders])

  if (showTrialExpired) {
    return (
      <>
        <ErrorLayout title="Trial Expired" anonymous={false}>
          <Text>
            Your BuildPass trial expired on{' '}
            <Text strong>{moment(builder.trialExpiresAt).format('LL')}</Text>
          </Text>
          <Text>
            You can click the button below to chat to our team to extend your
            trial or upgrade{canSwitchBuilder ? ' - or switch company.' : '.'}
          </Text>
          <Flexbox gap="1rem" mt="1rem">
            <Button
              variant="primary"
              onClick={openLiveChat}
              icon={<IconMessageCircle2 size={16} />}
            >
              Open chat
            </Button>
            {canSwitchBuilder && (
              <Button
                variant="secondary"
                onClick={() => setIsSwitchModalOpen(true)}
                icon={<IconSwitch3 size={16} />}
              >
                Switch Company
              </Button>
            )}
          </Flexbox>
        </ErrorLayout>
        {canSwitchBuilder && (
          <BuilderSelectModal
            open={isSwitchModalOpen}
            closeModal={() => setIsSwitchModalOpen(false)}
          />
        )}
      </>
    )
  }

  return children
}
